import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  List,
  Row,
  Spin,
  Statistic,
  Table,
  Typography,
  notification,
} from 'antd';
import { ReloadOutlined, RightOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageTitle from 'components/page-title';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { paths } from 'routes/routes';
import { LEDGER_KIND_LABEL } from 'pages/star-shared/labels';
import { mapStarError } from 'pages/star-shared/star-error';
import { ItemAdmin, LedgerKind, StarDashboard } from 'pages/star-shared/types';
import { formatDateTime, formatNumber } from 'pages/star-shared/utils';
import StarAmount from 'pages/star-shared/components/star-amount';
import { colors } from 'theme/tokens';

const cardBody = { body: { padding: 16 } };

interface QueueTileProps {
  title: string;
  count: number;
  to: string;
  actionText: string;
}

const QueueTile = ({ title, count, to, actionText }: QueueTileProps) => (
  <Card styles={cardBody} style={{ height: '100%' }}>
    {/* Số lớn = heading navy (theme Statistic), như trang Tổng quan — không tô cam/xanh/đỏ. */}
    <Statistic title={title} value={count} />
    <Link to={to}>
      <Button type="link" style={{ paddingLeft: 0 }}>
        {actionText} <RightOutlined />
      </Button>
    </Link>
  </Card>
);

const kindColumns: ColumnsType<{ kind: string; amount: number }> = [
  {
    title: 'Loại giao dịch',
    dataIndex: 'kind',
    key: 'kind',
    render: (kind: LedgerKind) => LEDGER_KIND_LABEL[kind] || kind,
  },
  {
    title: 'Tổng Sao',
    dataIndex: 'amount',
    key: 'amount',
    align: 'right',
    render: (amount: number) => <StarAmount value={amount} signed />,
  },
];

const StarDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<StarDashboard | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.starsDashboard);
        if (!cancelled) setData(res?.data || null);
      } catch (err) {
        if (!cancelled) notification.error({ message: mapStarError(err) });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchDashboard();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const byKind = Object.entries(data?.byKind || {})
    .filter(([, amount]) => typeof amount === 'number' && amount !== 0)
    .map(([kind, amount]) => ({ kind, amount: amount as number }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <PageTitle title="Điểm Sao — Tổng quan" />
        <Button
          icon={<ReloadOutlined />}
          onClick={() => setRefreshKey((k) => k + 1)}
          loading={loading}
        >
          Tải lại
        </Button>
      </div>

      {loading && !data ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : !data ? (
        <Empty description="Chưa có dữ liệu tổng quan." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Typography.Text type="secondary">
            Tháng {data.month}
            {data.lastReconcileAt
              ? ` · Đối soát lần cuối: ${formatDateTime(data.lastReconcileAt)}`
              : ' · Chưa chạy đối soát'}
          </Typography.Text>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={8}>
              <Card styles={cardBody}>
                <Statistic
                  title="Sao phát tháng này"
                  value={data.issued}
                  formatter={(v) => `${formatNumber(Number(v))} ⭐`}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Card styles={cardBody}>
                <Statistic
                  title="Sao tiêu tháng này"
                  value={data.spent}
                  formatter={(v) => `${formatNumber(Number(v))} ⭐`}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Card styles={cardBody}>
                <Statistic
                  title="Đang lưu hành"
                  value={data.circulating}
                  formatter={(v) => `${formatNumber(Number(v))} ⭐`}
                />
              </Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={8}>
              <QueueTile
                title="Nhiệm vụ chờ duyệt"
                count={data.queue?.claims || 0}
                to={`${paths.starMissions}?tab=reviews`}
                actionText="Mở hàng chờ duyệt"
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <QueueTile
                title="Đơn đổi quà chờ giao"
                count={data.queue?.orders || 0}
                to={`${paths.starShop}?tab=orders`}
                actionText="Mở đơn đổi quà"
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <QueueTile
                title="Chờ duyệt xoá phạt"
                count={data.queue?.waives || 0}
                to={`${paths.starPenalties}?tab=waives`}
                actionText="Mở hàng chờ xoá phạt"
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={8}>
              <Card
                styles={cardBody}
                title="Món sắp hết"
                extra={<Link to={paths.starShop}>Cửa hàng</Link>}
                style={{ height: '100%' }}
              >
                {data.lowStock?.length ? (
                  <List
                    size="small"
                    dataSource={data.lowStock}
                    renderItem={(item: ItemAdmin) => (
                      <List.Item>
                        <span>{item.name}</span>
                        <span
                          style={{
                            color: (item.stock ?? 1) <= 0 ? colors.dangerInk : colors.warningInk,
                          }}
                        >
                          còn {item.stock ?? 0}
                        </span>
                      </List.Item>
                    )}
                  />
                ) : (
                  <Typography.Text type="secondary">Không có món nào sắp hết.</Typography.Text>
                )}
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card styles={cardBody} title="Cờ đỏ (đối soát)" style={{ height: '100%' }}>
                {data.flags?.length ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {data.flags.map((flag, index) => (
                      <Alert key={index} type="warning" showIcon message={flag} />
                    ))}
                  </div>
                ) : (
                  <Typography.Text type="secondary">Không có cảnh báo.</Typography.Text>
                )}
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card
                styles={cardBody}
                title="Theo loại giao dịch"
                // Chữ link ngắn để tiêu đề không bị cắt "…" khi thẻ hẹp (màn 1280, 3 thẻ một hàng).
                extra={<Link to={paths.starLedger}>Xem lịch sử</Link>}
                style={{ height: '100%' }}
              >
                {byKind.length ? (
                  <Table
                    size="small"
                    rowKey="kind"
                    pagination={false}
                    columns={kindColumns}
                    dataSource={byKind}
                  />
                ) : (
                  <Typography.Text type="secondary">Chưa có giao dịch trong tháng.</Typography.Text>
                )}
              </Card>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default StarDashboardPage;
